import React from "react";
import {Box, Select} from "@mantine/core";
import {IStimStep, TStimLocation} from "@/protocols/types/protocol";
import {IStimStepDefinition} from "../../../types/protocolDefinitions";
import classes from "../StepView.module.css";
import {NeuroTracSportsStimulator} from "@/protocols/common/NeuroTracSportsStimulator.tsx";
import {BscProgramsStimulator} from "@/protocols/common/BscProgramsStimulator.tsx";
import {TStimDeviceKind} from "@/common/types/StimDevice.ts";
import {DeepPartial} from "@/utils/types/DeepPartial.ts";
import {TStimConfig, TStimMetadata, TStimPolarity, TStimShape} from "@/common/types/TStimConfig.ts";
import {TWorkflow} from "../StepView";
import {StepDurationField} from "../fields/StepDurationField";

/** Union type for Stim steps (both execution and planning modes) */
type StimStep = IStimStep | IStimStepDefinition;

export interface StimStepViewProps {
    step: StimStep;
    onChange: (updates: DeepPartial<StimStep>) => void;
    workflow?: TWorkflow;
}

/**
 * View component for stimulation event steps.
 * This is the primary rendering for stim steps in all contexts.
 * Used in protocol execution, planning, and review modes.
 */
export const StimStepView: React.FC<StimStepViewProps> = ({
                                                              step,
                                                              onChange,
                                                              workflow = "session",
                                                          }) => {
    const isReadOnly = workflow === "review";

    const updateStimConfig = (configUpdates: DeepPartial<TStimConfig>) => {
        onChange({
            stimMetadata: {
                config: configUpdates
            }
        } as DeepPartial<StimStep>);
    };

    const updateStimMetadata = (metadataUpdates: DeepPartial<TStimMetadata>) => {
        onChange({
            stimMetadata: metadataUpdates
        });
    };
    console.log(step.stimMetadata)

    return (
        <Box style={{gridColumn: "1 / -1"}}>
            <div className={classes.stimStepViewField}>
                <Box style={{
                    display: "grid",
                    grid: `auto / min-content auto 1fr auto auto`,
                    columnGap: "1em",
                    alignItems: "center",
                }}>
                    <div className={classes.stimParamCol}>
                        <Select
                            label="Device"
                            size="xs"
                            placeholder="Select"
                            value={step.stimMetadata?.stimDevice?.kind || null}
                            data={[
                                {value: TStimDeviceKind.NeuroTracSports, label: "NeuroTrac Sports"},
                                {value: TStimDeviceKind.BscPrograms, label: "Bsc Programs"}
                            ]}
                            miw={"12em"}
                            disabled={isReadOnly}
                            onChange={(value) => {
                                if (value) {
                                    if (value === TStimDeviceKind.BscPrograms) {
                                        updateStimMetadata({
                                            stimDevice: {
                                                kind: value,
                                                name: "Bsc Programs",
                                                description: "Bsc Programs",
                                                channels: []
                                            },
                                            stimLocation: TStimLocation.Spine
                                        } as DeepPartial<TStimMetadata>);
                                    } else {
                                        updateStimMetadata({
                                            stimDevice: {
                                                kind: value as TStimDeviceKind,
                                                name: value,
                                                description: "",
                                                polarity: TStimPolarity.Anodic,
                                                shape: TStimShape.BIPHASIC,
                                                channels: ["Channel A", "Channel B"]
                                            },
                                        });
                                    }
                                }
                            }}
                        />
                    </div>
                    <Box/>
                    <div className={classes.stimParamCol}>
                        <Select
                            label="Location"
                            size="xs"
                            placeholder="Select"
                            value={step.stimMetadata?.stimLocation || null}
                            data={[
                                {value: "median", label: "Median"},
                                {value: "auricular", label: "Auricular"},
                                {value: "cervical", label: "Cervical"},
                                {value: "visceral", label: "Visceral"},
                                {value: "thoracic", label: "Thoracic"},
                                {value: "tens", label: "TENS"},
                                {value: "spine", label: "Spine"}
                            ]}
                            disabled={isReadOnly}
                            onChange={(value) => updateStimMetadata({stimLocation: value as TStimLocation})}
                        />
                    </div>
                    <div className={classes.stimParamCol}>
                        <StepDurationField
                            miw={"10rem"}
                            duration={step.stimMetadata?.treatmentDurationSec}
                            disabled={isReadOnly}
                            onChange={(duration) => updateStimMetadata({treatmentDurationSec: duration})}
                        />
                    </div>
                </Box>
                {step.stimMetadata?.stimDevice ?
                    <Box style={{
                        display: "grid",
                        grid: "auto auto / auto auto auto auto auto",
                        columnGap: "0.25em",
                        border: "var(--bios-border)",
                        padding: "var(--bios-padding)"
                    }}>
                        {step.stimMetadata?.stimDevice?.kind == TStimDeviceKind.NeuroTracSports ?
                            <NeuroTracSportsStimulator device={step.stimMetadata?.stimDevice}
                                                       config={step.stimMetadata?.config || {}}
                                                       onChange={updateStimConfig}
                            readonly={isReadOnly}/> :
                            step.stimMetadata?.stimDevice?.kind == TStimDeviceKind.BscPrograms ?
                                <BscProgramsStimulator device={step.stimMetadata?.stimDevice}
                                                       metadata={step.stimMetadata || {}}
                                                       onChange={updateStimMetadata}
                                readonly={isReadOnly}/> :
                                (<></>)}
                    </Box>
                    : <Box/>
                }
            </div>
        </Box>
    );
};